import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { useAgentPermissions } from "../../context/AgentPermissionsContext";
import { useNotification } from "../../context/NotificationContext";
import { useFilterMine } from "../../context/FilterMineContext";
import { agentsApi } from "../../services/api";
import OwlShooterGame from "../OwlShooterGame";
import NotificationBell from "../Notifications/NotificationBell";
import ErrorBoundary from "../ErrorBoundary";
import HelpInfo from "../Help/HelpInfo";
import { NAV_HELP } from "../Help/navHelpContent";
import SkipLink, { MAIN_CONTENT_ID } from "../a11y/SkipLink";

// Frontend-only easter egg, gated on this single account by email (never by
// display_name, which can change) — see OwlShooterGame.jsx for the game itself.
const OWL_GAME_EMAIL = "udi@prodigy.co.il";

export const NAV = [
  {
    to: "/dashboard",
    label: "לוח בקרה",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🏠</span>,
  },
  {
    to: "/tickets",
    label: "קריאות שירות",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📋</span>,
  },
  {
    to: "/customer-info",
    label: "מידעון",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📖</span>,
  },
  {
    to: "/projects",
    label: "פרויקטים",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📁</span>,
  },
  {
    to: "/maintenance",
    label: "תחזוקה",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🛠️</span>,
  },
  {
    to: "/reports",
    label: "דוחות",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📊</span>,
  },
  {
    to: "/companies",
    label: "חברות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🏢</span>,
  },
  {
    to: "/contacts",
    label: "אנשי קשר",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">👤</span>,
  },
  {
    to: "/managed-domains",
    label: "ניהול דומיינים",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🌐</span>,
  },
  {
    to: "/technicians",
    label: "טכנאים",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧑‍🔧</span>,
  },
  {
    to: "/helpdesk",
    label: "בית העובדים",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🛎️</span>,
  },
  {
    to: "/agents",
    label: "סוכנים",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🤖</span>,
  },
  {
    to: "/workforce",
    label: "כוח העבודה",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧭</span>,
  },
  {
    to: "/approval-center",
    label: "מרכז אישורים",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">✅</span>,
  },
  {
    to: "/agent-runs",
    label: "ריצות AI",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">⚡</span>,
  },
  {
    // FULL-0128 / PROD-OPS-042: the EVALUATE/IMPROVE half of MASTER_FLOW §15A. The four routes the
    // page reads are Admin/Owner-gated, so the nav entry follows the same role gate. The page is
    // read-only: it mounts no record, approval or promotion route.
    to: "/agent-evaluations",
    label: "סביבת הערכה",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧪</span>,
  },
  {
    to: "/model-transparency",
    label: "שקיפות מודל וספק",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧭</span>,
  },
  {
    to: "/recovery",
    label: "התאוששות",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">♻</span>,
  },
  {
    to: "/specialists",
    label: "מרחב מומחים",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧩</span>,
  },
  {
    to: "/specialists/eset",
    label: "מומחה ESET",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🦠</span>,
  },
  {
    to: "/specialists/m365",
    label: "מומחה M365 · Entra",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔷</span>,
  },
  {
    to: "/specialists/acronis",
    label: "מומחה Acronis",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🛡️</span>,
  },
  {
    to: "/attention-inbox",
    label: "תיבת תשומת לב",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📥</span>,
  },
  {
    to: "/conversations",
    label: "מרכז השיחות",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">💬</span>,
  },
  {
    to: "/handoffs",
    label: "מסירה לאדם",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🤝</span>,
  },
  {
    to: "/routing-desk",
    label: "שולחן הניתוב",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧭</span>,
  },
  {
    to: "/collaboration",
    label: "שיתוף פעולה",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🤝</span>,
  },
  {
    to: "/manager-console",
    label: "קונסולת המנהל",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧭</span>,
  },
  {
    to: "/work-evidence",
    label: "עבודה וראיות",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📜</span>,
  },
  {
    to: "/command-center",
    label: "מרכז פיקוד",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">⬡</span>,
  },
  {
    to: "/remote-operations",
    label: "פעולות מרחוק",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🖥️</span>,
  },
  {
    to: "/system-map",
    label: "איך זה עובד",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🗺️</span>,
  },
  {
    to: "/handoff-center",
    label: "מרכז ההעברות",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔁</span>,
  },
  {
    to: "/pilot-acceptance",
    label: "קבלת פיילוט",
    adminOnly: false,
    agentPermission: "agents_view",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧭</span>,
  },
  {
    to: "/pilot-feedback",
    label: "פידבק פיילוט",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📝</span>,
  },
  {
    to: "/agent-permissions",
    label: "הרשאות AI",
    adminOnly: false,
    agentPermission: "agents_manage",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔑</span>,
  },
  {
    to: "/knowledge",
    label: "בסיס ידע",
    adminOnly: false,
    agentPermission: "agents_manage",
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📚</span>,
  },
  {
    to: "/connections",
    label: "חיבורים",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔌</span>,
  },
  {
    to: "/platform-health",
    label: "בריאות פלטפורמה",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🩺</span>,
  },
  {
    to: "/employee-builder",
    label: "בניית עובד",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🧙</span>,
  },
  {
    to: "/scheduled-checks",
    label: "בדיקות מתוזמנות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">⏱</span>,
  },
  {
    to: "/scheduled-operations",
    label: "תפעול מתוזמן",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📅</span>,
  },
  {
    to: "/bug-hunter",
    label: "ציד באגים",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🐛</span>,
  },
  {
    to: "/backup-guardian",
    label: "שומר גיבויים",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">💾</span>,
  },
  {
    to: "/guardian",
    label: "מרכז השומרים",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🛡️</span>,
  },
  {
    to: "/alerts",
    label: "התראות AI",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🚨</span>,
  },
  {
    to: "/skills",
    label: "מיומנויות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🎯</span>,
  },
  {
    to: "/feedback",
    label: "איכות AI",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">📊</span>,
  },
  {
    to: "/tech-scout",
    label: "סיור טכנולוגיות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔍</span>,
  },
  {
    to: "/innovation-review",
    label: "סקירת חדשנות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔬</span>,
  },
  {
    href: "https://command.osinet.ai",
    label: "Command Osinet",
    adminOnly: false,
    external: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">💻</span>,
  },
  {
    to: "/help",
    label: "מרכז עזרה",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">❓</span>,
  },
  {
    to: "/activity-log",
    label: "יומן פעולות",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🕘</span>,
  },
  {
    to: "/notification-settings",
    label: "הגדרות התראות",
    adminOnly: false,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🔔</span>,
  },
  {
    // FULL-0159 / PROD-UX-073: the read-only Admin/Configuration V2 index. Every route it reads is
    // already Admin/Owner-gated (or uses the Admin's implicit agents_manage grant), so the nav entry
    // follows the same gate. The page performs no write.
    to: "/administration",
    label: "ניהול והגדרות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">🗂️</span>,
  },
  {
    to: "/settings",
    label: "הגדרות",
    adminOnly: true,
    icon: <span className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-lg" aria-hidden="true">⚙️</span>,
  },
];

// HELPDESK-1.0 scope (2026-10-29): the daily helpdesk is the product. Agent/"digital employee"
// surfaces measured as unused in production (9 agent runs ever, none since 2026-10-02) are kept in
// code but hidden from the sidebar as "labs". A user can reveal them per browser with ?labs=1
// (and hide again with ?labs=0). Permission gates still apply to every lab item. Routes stay
// reachable by URL; nothing is deleted.
export const LAB_ROUTES = new Set([
  "/helpdesk", "/agents", "/workforce", "/approval-center", "/agent-runs", "/agent-evaluations",
  "/model-transparency", "/recovery", "/specialists", "/specialists/eset", "/specialists/m365",
  "/specialists/acronis", "/attention-inbox", "/conversations", "/handoffs", "/routing-desk",
  "/collaboration", "/manager-console", "/work-evidence", "/command-center", "/remote-operations",
  "/system-map", "/handoff-center", "/pilot-acceptance", "/pilot-feedback", "/agent-permissions",
  "/connections", "/platform-health", "/employee-builder", "/scheduled-operations", "/bug-hunter",
  "/backup-guardian", "/guardian", "/learning-center", "/skills", "/employee-skills", "/feedback",
  "/tech-scout", "/innovation-review",
]);

const LABS_STORAGE_KEY = "prodigy.showLabs";

export function readShowLabs(search) {
  try {
    const flag = new URLSearchParams(search || "").get("labs");
    if (flag === "1") window.localStorage.setItem(LABS_STORAGE_KEY, "1");
    if (flag === "0") window.localStorage.removeItem(LABS_STORAGE_KEY);
    return window.localStorage.getItem(LABS_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

const FEED_ICONS = { ticket: "📨", reply: "✉️" };

function formatFeedTime(timestamp) {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "עכשיו";
  if (minutes < 60) return `לפני ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours} שעות`;
  return `לפני ${Math.floor(hours / 24)} ימים`;
}

function UpdatesPanel({ feed }) {
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="border-t border-gray-700 px-3 pt-3 pb-2" dir="rtl">
      <div className="flex items-center gap-1.5 px-1 mb-2">
        <span className="leading-none">🔔</span>
        <span style={{ fontSize: 14, fontWeight: 600 }} className="text-gray-400 uppercase tracking-wider">עדכונים</span>
      </div>

      {feed.length === 0 ? (
        <p style={{ fontSize: 13 }} className="text-gray-600 text-center py-1 italic">אין עדכונים עדיין</p>
      ) : (
        <div className="space-y-0.5">
          {feed.map((item) => (
            <button
              key={item.id}
              onClick={() => item.url && navigate(item.url)}
              className={`w-full text-right px-2 py-2 rounded-lg transition-colors leading-snug ${
                item.isNew
                  ? "bg-gray-700 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-white"
              } ${item.url ? "cursor-pointer" : "cursor-default"}`}
            >
              <div className="flex items-start gap-2">
                <span className="shrink-0 leading-none mt-0.5">
                  {FEED_ICONS[item.type] ?? "ℹ️"}
                </span>
                <div className="min-w-0 flex-1">
                  <p style={{ fontSize: 13 }} className="truncate font-medium">{item.message}</p>
                  {item.sender && (
                    <p style={{ fontSize: 12 }} className="truncate text-gray-400 mt-0.5">
                      מ: {item.sender}
                    </p>
                  )}
                  <p style={{ fontSize: 11 }} className={`mt-0.5 ${item.isNew ? "text-gray-300" : "text-gray-500"}`}>
                    {formatFeedTime(item.timestamp)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Chevron icons for collapse toggle
const IconChevronLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);
const IconChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);
const IconLogout = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 17l5-5-5-5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12H9" />
  </svg>
);
const IconPerson = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { can: canAgent } = useAgentPermissions();
  const { pathname, search } = useLocation();
  const { feed } = useNotification();
  const { showOnlyMine, toggle } = useFilterMine();
  const [isWideScreen, setIsWideScreen] = useState(() => window.innerWidth >= 1536);
  const [showOwlGame, setShowOwlGame] = useState(false);
  const isOwlGameUser = user?.email?.toLowerCase() === OWL_GAME_EMAIL;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem("sidebar-collapsed");
      // null = first visit → default collapsed; "false" = user explicitly expanded → respect it
      return stored === null ? true : stored === "true";
    } catch { return true; }
  });

  const toggleSidebar = () => {
    if (!isWideScreen) return;
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("sidebar-collapsed", String(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth >= 1536);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const initials = (user?.display_name ?? "")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const { data: _agentsList = [], isError: _agentsListFailed } = useQuery({
    queryKey: ["agents-layout-badge"],
    queryFn: () => agentsApi.list(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: canAgent("agents_view"),
  });
  // A failed fetch used to read the same as "zero pending runs": the badge on the single most
  // globally-rendered nav item silently disappeared instead of showing that the count is unknown.
  const pendingRunsCount = _agentsListFailed ? null : _agentsList.reduce((s, a) => s + (a.pending_runs_count || 0), 0);

  const showLabs = readShowLabs(search);
  const visibleNav = NAV.filter((item) => {
    if (item.to && LAB_ROUTES.has(item.to) && !showLabs) return false;
    if (item.agentPermission) return canAgent(item.agentPermission);
    if (item.adminOnly) return isAdmin;
    return true;
  });
  const sidebarCollapsed = !isWideScreen || collapsed;

  return (
    <div className="min-h-screen flex bg-gray-50" style={{ overflowX: 'hidden' }}>
      {/* First focusable element: reaches the page content without tabbing the whole sidebar. */}
      <SkipLink />

      {/* Sidebar */}
      <aside
        className="fixed top-0 h-full bg-gray-900 text-white flex flex-col z-30 shadow-lg overflow-x-hidden transition-all duration-300 ease-in-out"
        style={{ right: 0, width: sidebarCollapsed ? "4rem" : "16rem" }}
      >
        {/* Header */}
        <div
          className={`border-b border-gray-700 shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? "px-2 py-4" : "px-6 py-5"
          }`}
        >
          {sidebarCollapsed ? (
            /* Collapsed header: expand button → avatar → filter toggle */
            <div className="flex flex-col items-center gap-2.5">
              {isWideScreen && (
                <button
                  onClick={toggleSidebar}
                  title="הרחב סרגל"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                  type="button"
                >
                  <IconChevronLeft />
                </button>
              )}

              <div
                className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-sm select-none cursor-default"
                title={user?.display_name}
              >
                {initials}
              </div>

              <button
                onClick={toggle}
                title={showOnlyMine ? "מציג רק שלי — לחץ להציג הכל" : "הצג רק את שלי"}
                type="button"
                className={`relative w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                  showOnlyMine
                    ? "bg-blue-600 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`}
              >
                <IconPerson />
                {showOnlyMine && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-blue-200 border border-blue-600" />
                )}
              </button>
            </div>
          ) : (
            /* Expanded header: logo + collapse button + user name + filter toggle */
            <>
              <div className="flex items-center justify-between gap-2">
                <Link to="/dashboard" className="font-bold text-lg tracking-tight whitespace-nowrap min-w-0 truncate">
                  Prodigy Support
                </Link>
                <button
                  onClick={toggleSidebar}
                  title="צמצם סרגל"
                  type="button"
                  className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <IconChevronRight />
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-1 truncate">{user?.display_name}</p>

              <button
                onClick={toggle}
                className="flex items-center gap-2 w-full mt-2 rounded-lg px-2 py-1.5 hover:bg-gray-800"
                type="button"
                dir="rtl"
              >
                <span
                  style={{
                    fontSize: 12,
                    flex: 1,
                    textAlign: "right",
                    transition: "color 0.2s ease, font-weight 0.1s ease",
                    color: showOnlyMine ? "#60a5fa" : "#9ca3af",
                    fontWeight: showOnlyMine ? 700 : 400,
                  }}
                >
                  הצג רק את שלי
                </span>
                {/* RTL toggle: OFF = dot on right, ON = dot on left */}
                <div
                  style={{
                    position: "relative",
                    width: 38,
                    height: 22,
                    borderRadius: 11,
                    flexShrink: 0,
                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                    backgroundColor: showOnlyMine ? "#2563eb" : "#94a3b8",
                    boxShadow: showOnlyMine ? "0 0 0 3px rgba(37,99,235,0.4)" : "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 3,
                      left: 3,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
                      transition: "transform 0.2s ease",
                      transform: showOnlyMine ? "translateX(0px)" : "translateX(16px)",
                    }}
                  />
                </div>
              </button>
            </>
          )}
        </div>

        {/* Scrollable middle: nav + updates feed */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          <nav aria-label="ניווט ראשי" className="py-3">
            {visibleNav.map(({ to, href, label, icon, external }) => {
              const isActive = !external && pathname.startsWith(to);
              const rowCls = `flex items-center gap-1 py-3 text-base transition-colors whitespace-nowrap ${
                sidebarCollapsed ? "justify-center px-0" : "px-6"
              } ${
                isActive
                  ? "bg-gray-700 text-white font-semibold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white"
              }`;

              const badge = to !== "/agent-runs" ? null
                : pendingRunsCount === null
                ? <span role="alert" title="לא ניתן לטעון את מספר הריצות הממתינות" className="shrink-0 text-xs font-bold bg-gray-500 text-white rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">?</span>
                : pendingRunsCount > 0
                ? <span className="shrink-0 text-xs font-bold bg-orange-500 text-white rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">{pendingRunsCount}</span>
                : null;

              const linkContent = (
                <>
                  {icon}
                  {!sidebarCollapsed && <span className="truncate">{label}</span>}
                </>
              );
              const linkCls = "flex items-center gap-3 flex-1 min-w-0";

              // The help affordance is a sibling of the Link/anchor, never
              // nested inside it: a <button> inside an <a> is invalid HTML
              // and breaks keyboard/screen-reader navigation.
              return (
                <div key={to || href} className={rowCls}>
                  {external ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkCls}
                      title={sidebarCollapsed ? label : undefined}
                    >
                      {linkContent}
                    </a>
                  ) : (
                    <Link
                      to={to}
                      className={linkCls}
                      title={sidebarCollapsed ? label : undefined}
                    >
                      {linkContent}
                    </Link>
                  )}
                  {!sidebarCollapsed && badge}
                  {!sidebarCollapsed && (
                    <HelpInfo label={label} content={NAV_HELP[to || href]} />
                  )}
                </div>
              );
            })}
          </nav>

          {!sidebarCollapsed && <UpdatesPanel feed={feed} />}
        </div>

        {/* Footer */}
        <div
          className={`border-t border-gray-700 shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? "px-2 py-3" : "px-4 py-4"
          }`}
        >
          {sidebarCollapsed ? (
            <button
              onClick={logout}
              title="התנתקות"
              className="w-full flex justify-center p-2 rounded-lg hover:bg-red-900/30 text-red-400/70 hover:text-red-300 transition-colors"
            >
              <IconLogout />
            </button>
          ) : (
            <>
              {isOwlGameUser ? (
                <p
                  className="text-gray-400 text-xs mb-2 truncate cursor-pointer hover:text-white transition-colors"
                  onClick={() => setShowOwlGame(true)}
                  title="🦉"
                >
                  {user?.display_name}
                </p>
              ) : (
                <p className="text-gray-400 text-xs mb-2 truncate">{user?.display_name}</p>
              )}
              <button
                onClick={logout}
                className="w-full bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded text-xs transition-colors text-right"
              >
                יציאה
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main content — expands when sidebar collapses */}
      <div
        className="flex-1 min-h-screen transition-all duration-300 ease-in-out"
        style={{ marginRight: sidebarCollapsed ? "4rem" : "16rem", minWidth: 0 }}
      >
        {/* Top bar — hosts the notification bell (panel opens top-center) */}
        <header
          className="sticky top-0 z-20 flex items-center gap-2 h-14 px-3 xl:px-4 bg-white/90 backdrop-blur border-b border-gray-200"
          dir="rtl"
        >
          <div className="flex-1" />
          <NotificationBell />
        </header>
        <main id={MAIN_CONTENT_ID} tabIndex={-1} className="p-3 xl:p-4 2xl:p-6">
          <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
        </main>
      </div>

      {isOwlGameUser && showOwlGame && (
        <OwlShooterGame onClose={() => setShowOwlGame(false)} />
      )}
    </div>
  );
}

